// The campaign tags of the wahala.io link a phone came in on (docs/wahala-io-urls.md): utm_source,
// utm_medium, utm_campaign, utm_content and the website's src (qr, poster, share, l-{landing}).
// Read once at boot and taken out of the address bar, like an ad's click id; kept on this phone
// until it makes its guest, which hands them to the api (the new player is the campaign's). A newer
// tagged landing replaces an older one. A player who already has an account is never claimed.
// Standalone only: inside the Lagos Life frame there are no links of ours.
import { tagValue } from '@wahala/invite/links';

const KEY = 'wahala.campaign';
/** Like an ad click: a phone that waits longer than this to make its player is not the campaign's. */
const KEEP_MS = 90 * 86_400_000;
/** Our field for each parameter on the link. */
const PARAMS = {
  utm_source: 'source',
  utm_medium: 'medium',
  utm_campaign: 'campaign',
  utm_content: 'content',
  src: 'src',
} as const;

export type Campaign = Partial<Record<(typeof PARAMS)[keyof typeof PARAMS], string>>;

/** Called once at boot: keep a tagged landing's tags and drop them from the URL. */
export function takeCampaign(): void {
  const q = new URLSearchParams(location.search);
  const tags: Campaign = {};
  let seen = false;
  for (const [param, field] of Object.entries(PARAMS)) {
    if (!q.has(param)) continue;
    seen = true;
    const v = tagValue(q.get(param) ?? '');
    if (v) tags[field] = v;
    q.delete(param);
  }
  if (!seen) return;
  const rest = q.toString();
  history.replaceState(history.state, '', `${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`);
  if (!Object.keys(tags).length) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ tags, at: Date.now() }));
  } catch {
    // storage blocked: the player plays all the same
  }
}

/** The kept tags, for a new guest; null when there are none or they are too old. */
export function campaignTags(): Campaign | null {
  try {
    const kept = JSON.parse(localStorage.getItem(KEY) ?? 'null') as { tags?: unknown; at?: unknown } | null;
    if (!kept || typeof kept.at !== 'number' || Date.now() - kept.at > KEEP_MS) return null;
    if (typeof kept.tags !== 'object' || !kept.tags) return null;
    const tags: Campaign = {};
    for (const field of Object.values(PARAMS)) {
      const v = (kept.tags as Record<string, unknown>)[field];
      if (typeof v === 'string' && /^[a-z0-9-]{1,60}$/.test(v)) tags[field] = v;
    }
    return Object.keys(tags).length ? tags : null;
  } catch {
    return null;
  }
}

/** The guest is made: the tags have done their work. */
export function forgetCampaign(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // storage blocked
  }
}
